import { exists } from 'helpers/exists'
import { IService } from 'services'
import {
  CommonStoryRow,
  NormalizedStoryImageElement,
  StoryGenderType,
  StoryRow,
} from 'services/providers/iris/entities/stories/storyRows/storyRow.normalizer.types'
import Idol from 'store/entities/Idol/Idol.store'
import Outfit from 'store/entities/Outfit/Outfit.store'

function getOutfitIdsFromStoryRows(storyRows: StoryRow[]) {
  const ids = new Set<string>()

  storyRows
    .filter((row): row is CommonStoryRow => row.type === 'outfit')
    .flatMap(row => row.elementRows)
    .forEach(rowElement => {
      if (rowElement[0].type === 'outfit') {
        ids.add(rowElement[0].outfitId)
      }
    })

  return [...ids]
}

function getAllPrimaryProductIds(outfits: Outfit[]) {
  return outfits.flatMap(outfit =>
    outfit.products
      .filter(({ priority }) => priority === 'primary')
      .map(({ id }) => id),
  )
}

function groupProductIdsByPriority(outfit: Outfit) {
  const primaryProductIds = outfit.products
    .filter(({ priority }) => priority === 'primary')
    .map(({ id }) => id)
  const nonPrimaryProductIds = outfit.products
    .filter(({ priority }) => priority !== 'primary')
    .map(({ id }) => id)

  return { primaryProductIds, nonPrimaryProductIds }
}

function storyRowHasOutfit(storyRow: StoryRow, outfit: Outfit) {
  return (
    storyRow.type === 'outfit' &&
    storyRow.elementRows
      .flatMap(row => row)
      .some(
        element => element.type === 'outfit' && element.outfitId === outfit.id,
      )
  )
}

function convertOutfitRowToSingleTileImageRow(
  outfitRow: CommonStoryRow,
  {
    outfit,
    idol,
    nonPrimaryProductIds,
  }: {
    outfit: Outfit
    idol: Idol | null
    nonPrimaryProductIds: string[]
  },
) {
  outfitRow.type = 'tile'
  outfitRow.tileRatio = '3:4'
  outfitRow.productIds = nonPrimaryProductIds.map(id => String(id))

  outfitRow.elementRows
    .flatMap(row => row)
    .filter(element => element.type === 'outfit')
    .forEach(element => {
      ;(element as unknown as NormalizedStoryImageElement).type = 'image'
      ;(element as unknown as NormalizedStoryImageElement).image = {
        id: outfit.portraitImages[0],
        entityType: 'image',
        hash: outfit.portraitImages[0],
        isLandscape: false,
      }
      ;(element as unknown as NormalizedStoryImageElement).linkedResource = {
        ...outfit,
        entityType: 'outfit',
        active: true,
        backgroundType: 'visible',
        idolId: parseInt(outfit.idolId),
        idol: exists(idol)
          ? {
              ...idol,
              entityType: 'idol',
              gender: idol.gender as StoryGenderType,
              profile_id: parseInt(idol.id),
            }
          : null,
        productIds: outfit.productIds,
        similarFlaggedProductIds: outfit.productIds.map(id => parseInt(id)),
        gender: outfit.gender as StoryGenderType,
      }
    })
}

export async function enhanceWithStoryRowOutfits(
  service: IService,
  storyRows: StoryRow[],
): Promise<StoryRow[]> {
  const allOutfitIds = getOutfitIdsFromStoryRows(storyRows)

  if (allOutfitIds.length === 0) {
    return storyRows
  }

  const { outfits: allOutfits } = await service.outfits.requestByIds([
    ...new Set(allOutfitIds),
  ])

  const allPrimaryProductIds = getAllPrimaryProductIds(allOutfits)

  if (allPrimaryProductIds.length > 0) {
    await service.products.requestProductsByIds(allPrimaryProductIds)
  }

  let remainingProductIdsToFetch: string[] = []

  for (const outfit of allOutfits) {
    const { primaryProductIds, nonPrimaryProductIds } =
      groupProductIdsByPriority(outfit)

    const outfitStoryRow = storyRows.find((row): row is CommonStoryRow =>
      storyRowHasOutfit(row, outfit),
    )

    if (!exists(outfitStoryRow)) {
      continue
    }

    const availablePrimaryProductIds =
      service.products.getAvailableIds(primaryProductIds)

    const hasAvailablePrimaryProducts = availablePrimaryProductIds.length > 0

    if (hasAvailablePrimaryProducts) {
      remainingProductIdsToFetch =
        remainingProductIdsToFetch.concat(nonPrimaryProductIds)
    } else {
      const idol = service.outfits.getIdolById(outfit.idolId)

      convertOutfitRowToSingleTileImageRow(outfitStoryRow, {
        outfit,
        idol,
        nonPrimaryProductIds,
      })
    }
  }

  if (remainingProductIdsToFetch.length > 0) {
    await service.products.requestProductsByIds(remainingProductIdsToFetch)
  }

  return storyRows
}
